import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Col, Container, Row, Card, CardHeader, CardBody, Input, Label, Button } from "@/Components/ui";
import Select from "react-select";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MyEditor from "../../../Components/editor/MyEditor";
import "quill/dist/quill.snow.css";
import { API } from "../../../api/client";
import { SITE_ID } from "../../../assets/meta/meta";
import moment from "moment";
import PeriodItem from "./PeriodItem";
import ReactDragListView from "react-drag-listview";
import toast, { Toaster } from 'react-hot-toast';
import DeleteModal from "../../../Components/Common/DeleteModal";
import { findMissingRequiredFields, focusFirstMissingField, formatRequiredMessage } from "@/utils/formRequired";
import { dialogService } from "@/utils/dialogService";
import { buildGroupedPricePayload, getGroupedPriceState } from "./pricePolicy";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";

const formatDateTimeLocalValue = (value) => {
  if (!value)
    return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime()))
    return "";
  const timezoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
};

const PACKAGE_FIELD_LABELS = {
  tagline: "태그라인",
  descript: "상세정보",
  using_info: "이용안내",
  instruction: "유의사항",
};

const formatPackageSaveError = (message) => {
  if (!message) {
    return "알 수 없는 오류가 발생했습니다.";
  }

  return Object.entries(PACKAGE_FIELD_LABELS).reduce((currentMessage, [fieldName, label]) => {
    return currentMessage.replace(new RegExp(fieldName, "g"), label);
  }, message);
};

const ReservationAddProduct = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isCopyMode = searchParams.get("copy") === "true";
    const seatImgInputRef = React.useRef(null); // seat Image
    const detailImgInputRef = React.useRef(null); // detail Image
    const [state, setState] = React.useState("노출중");
    const [name, setName] = React.useState("패키지");
    const [tagline, setTagline] = React.useState("");
    const [count, setCount] = React.useState(1);
    const [hasSpot, setHasSpot] = React.useState(false);
    const [minDate, setMinDate] = React.useState(1);
    const [maxDate, setMaxDate] = React.useState(1);
    // const [exceedPrice, setExceedPrice] = React.useState(0);
    const [descript, setDescript] = React.useState("");
    const [usingInfo, setUsingInfo] = React.useState("");
    const [instruction, setInstruction] = React.useState("");
    const [seatImg, setSeatImg] = React.useState(null);
    const [detailImg, setDetailImg] = React.useState(null);
    const [loadedSeatImg, setLoadedSeatImg] = useState(null);
    const [loadedDetailImg, setLoadedDetailImg] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteContext, setDeleteContext] = useState(null); // { type, id, title, message }
    // Loading states
    const [isPackageSaving, setIsPackageSaving] = useState(false);
    const [isPeriodSaving, setIsPeriodSaving] = useState(false);
    const [isPriceSaving, setIsPriceSaving] = useState(false);
    const [isImageProcessing, setIsImageProcessing] = useState(false);
    const [periods, setPeriods] = React.useState([]);
    // const [price, setPrice] = React.useState({});
    const [advancedSettings, setAdvancedSettings] = useState(false);
    const [allowFreeBooking, setAllowFreeBooking] = useState(false);
    const [stdPersonnel, setStdPersonnel] = React.useState(0);
    const [maxPersonnel, setMaxPersonnel] = React.useState(0);
    const [maxPersonnelDay, setMaxPersonnelDay] = React.useState(0);
    const [priority, setPriority] = React.useState(0);
    const [optionRequired, setOptionRequired] = React.useState(false);
    const optionRequiredTypeSelect = [
        { value: 'goods', label: '상품' },
        { value: 'extra', label: '추가 인원' },
        { value: 'extratable', label: '추가 테이블' },
        { value: 'deposit', label: '청소보증금' },
    ];
    const [optionRequiredType, setOptionRequiredType] = React.useState(null);
    const [orderAvailableAt, setOrderAvailableAt] = React.useState("");
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
    const markDirty = React.useCallback(() => setHasUnsavedChanges(true), []);
    const { confirmDiscardChanges } = useUnsavedChangesGuard({
        isDirty: hasUnsavedChanges,
        confirmNavigation: dialogService.confirm,
    });
    const isLodgePackage = useMemo(() => {
        return Number(minDate) > 1 || Number(maxDate) > 1;
    }, [minDate, maxDate]);
    const handleImageFiles = (e, type) => {
      const files = Array.from(e.target.files || []) as File[];
      if (files.length === 0)
        return;
      markDirty();
      setIsImageProcessing(true);
      Promise.all(files.map((file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === "string")
            resolve(reader.result);
          else
            reject(new Error("이미지 파일을 읽지 못했습니다."));
        };
        reader.onerror = () => reject(new Error("이미지 파일을 읽지 못했습니다."));
        reader.readAsDataURL(file);
      })))
        .then((imgFiles) => {
        if (type === "seat")
          setSeatImg(imgFiles);
        else if (type === "detail")
          setDetailImg(imgFiles);
      })
        .catch((error) => {
        console.error("Image processing failed", error);
        toast.error(error?.message || "이미지 처리 중 오류가 발생했습니다.");
      })
        .finally(() => {
        setIsImageProcessing(false);
      });
    };
    const [optionProducts, setOptionProducts] = React.useState([]);
    const [selectedMulti, setselectedMulti] = React.useState(null); // option 관련 state
    function handleMulti(selectedMulti) {
        // console.log(selectedMulti)
        markDirty();
        setselectedMulti(selectedMulti);
    }
    const updateDatas = async (id) => {
        if (id !== "0") {
            const res = await API.get(`/package/items/${id}/`);
            if (res.data) {
                fillStates(res.data, isCopyMode);
            }
        }
    };
    useEffect(() => {
        if (id) {
            updateDatas(id);
        }
    }, [id, isCopyMode]);
    useEffect(() => {
        API.get(`/package/options/`)
            .then((res) => {
            console.log("options products...", res.data);
            if (res.data && res.data.length)
                setOptionProducts(res.data.map((item) => {
                    return { value: item.id, label: `${item.name}: ${item.price}` };
                }));
        })
            .catch(async (error) => await dialogService.handleAlert(error.message, { force: "error" }));
    }, []);
    const refetchPeriods = () => {
        API.get(`/sites/periods/`)
            .then((res) => {
            // Sort by priority (descending)
            const sorted = res.data.sort((a, b) => b.priority - a.priority);
            setPeriods(sorted);
        })
            .catch(async (error) => await dialogService.handleAlert(error.message, { force: "error" }));
    };
    useEffect(() => {
        refetchPeriods();
    }, [id]);
    const fillStates = (data, copyMode = false) => {
        setState(data["state"]);
        setName(copyMode ? data["name"] + " (복사)" : data["name"]);
        setTagline(data["tagline"] ?? "");
        setCount(data["count"]);
        setHasSpot(data["has_spots"]);
        setMinDate(data["min_date"]);
        setMaxDate(data["max_date"]);
        setStdPersonnel(data["std_personnel"]);
        setMaxPersonnel(data["max_personnel"]);
        // setExceedPrice(data["exceed_price"]);
        setDescript(data["descript"] ?? "");
        setUsingInfo(data["using_info"] ?? "");
        setInstruction(data["instruction"] ?? "");
        // setPrice(data["price"]);
        // console.log("fillStates", data);
        // console.log("selected options:", data["options"])
        setMaxPersonnelDay(data["max_personnel_day"]);
        setAllowFreeBooking(data["allow_free_booking"]);
        setPriority(data["priority"]);
        setOptionRequired(data["option_required"]);
        setOptionRequiredType(data["option_required_type"] ? optionRequiredTypeSelect.find((item) => item.value === data["option_required_type"]) : null);
        setOrderAvailableAt(formatDateTimeLocalValue(data["order_available_start_at"]));
        // Don't copy images in copy mode - user should upload new ones
        if (!copyMode) {
            if (data["seat_img"])
                setLoadedSeatImg(data["seat_img"]);
            if (data["images"])
                setLoadedDetailImg(data["images"]);
        }
        setselectedMulti(data["options"].map((item) => {
            return { value: item.id, label: `${item.name}: ${item.price}` };
        }));
    };
    const sendData = (e) => {
        if (periods.length === 0) {
            // 기간 설정 폼으로 redirect하기
            toast.error("기간 설정을 먼저 해주세요. 기간을 설정할 수 없다면 관리자에게 문의해주세요");
            return;
        }
        if (isLodgePackage) {
            if (Number(minDate) < 2 || Number(maxDate) < 2) {
                toast.error("숙박 상품은 최소 2일 이상으로 설정해주세요.");
                return;
            }
        }
        const payload = checkData();
        if (!payload)
            return;
        setIsPackageSaving(true);
        API.post(`/package/0/`, payload)
            .then(async (res) => {
            console.log("sendData", res);
            if (res.data["result"] === "ok") {
                const newId = res.data["id"];
                if (isCopyMode) {
                    try {
                        const pricePromises = [];
                        periods.forEach((period) => {
                            const sourcePrice = period.package_prices.find((p) => Number(p.package_id) === Number(id));
                            if (sourcePrice) {
                                const normalizedPrice = buildGroupedPricePayload(getGroupedPriceState(sourcePrice));
                                pricePromises.push(API.post(`/package/package-price/`, {
                                    period: period.id,
                                    package: newId,
                                    ...normalizedPrice,
                                }));
                            }
                        });
                        if (pricePromises.length > 0) {
                            await Promise.all(pricePromises);
                            toast.success("상품 및 가격 정보 복사 완료!");
                        }
                        else {
                            toast.success("상품이 생성되었습니다! (가격 정보 없음)");
                        }
                    }
                    catch (err) {
                        console.error("Price copy failed", err);
                        toast.error("상품은 생성되었으나 가격 복사에 실패했습니다.");
                    }
                }
                else {
                    toast.success("상품이 생성되었습니다!");
                }
                setHasUnsavedChanges(false);
                // navigate(`/reservation-products`);
                navigate(`/reservation-add-product/${newId}`);
            }
            else {
                toast.error("상품 생성에 실패했습니다.");
            }
        })
            .catch((error) => {
            toast.error(`상품 생성 실패: ${formatPackageSaveError(error.message)}`);
        })
            .finally(() => {
            setIsPackageSaving(false);
        });
    };
    const updateData = (e) => {
        if (periods.length === 0) {
            // 기간 설정 폼으로 redirect하기
            toast.error("기간 설정을 먼저 해주세요.");
            return;
        }
        const payload = checkData();
        if (!payload)
            return;
        setIsPackageSaving(true);
        API.put(`/package/${id}/`, payload)
            .then((res) => {
            console.log("updateData", res);
            if (res.data["result"] === "ok") {
                toast.success("상품이 수정되었습니다!");
                setHasUnsavedChanges(false);
                navigate(`/reservation-products`);
            }
            else {
                toast.error("상품 수정에 실패했습니다.");
            }
        })
            .catch((error) => {
            toast.error(`상품 수정 실패: ${formatPackageSaveError(error.message)}`);
        })
            .finally(() => {
            setIsPackageSaving(false);
        });
    };
    const checkData = () => {
        const missingRequiredFields = findMissingRequiredFields([
            { label: "상품명", value: name, elementId: "productName" },
        ]);
        if (missingRequiredFields.length > 0) {
            toast.error(formatRequiredMessage(missingRequiredFields));
            focusFirstMissingField(missingRequiredFields);
            return null;
        }
        const data = {
            name: name,
            tagline: tagline.trim() || null,
            count: count,
            min_date: minDate,
            max_date: maxDate,
            std_personnel: stdPersonnel,
            max_personnel: maxPersonnel,
            // exceed_price: exceedPrice,
            max_personnel_day: maxPersonnelDay,
            allow_free_booking: allowFreeBooking,
            price: {},
            // "price": getPriceObj(),
            has_spots: hasSpot,
            state: state,
            descript: descript,
            using_info: usingInfo,
            instruction: instruction,
            seat_img: seatImg,
            image: detailImg,
            options: selectedMulti ? selectedMulti.map((item) => String(item.value)) : [],
            priority: priority,
            option_required: optionRequired,
            option_required_type: optionRequiredType?.value || null,
            order_available_start_at: orderAvailableAt || null,
        };
        return data;
    };
    // --- Period & Price Handlers ---
    const handlePeriodUpdate = (_body) => {
        setIsPeriodSaving(true);
        API.put(`/sites/periods/${_body.id}/`, { ..._body, site: SITE_ID })
            .then((res) => {
            // refetchPeriods(); // Refetching breaks the drag state / expansion state potentially. Better to update local state or just refetch for safety.
            // For smoother UX, we might want to update local state, but refetching is safer for priority updates.
            refetchPeriods();
            toast.success("기간이 저장되었습니다.");
        })
            .catch((error) => {
            toast.error(`기간 저장 실패: ${error.message}`);
        })
            .finally(() => {
            setIsPeriodSaving(false);
        });
    };
    // Add a default period
    const handlePeriodAdd = () => {
        // Create a default period
        const defaultData = {
            name: "새 기간",
            period_type: "비수기",
            start_date: moment().format("YYYY-MM-DD"),
            end_date: moment().add(1, 'month').format("YYYY-MM-DD"),
            priority: 0,
            site: SITE_ID
        };
        setIsPeriodSaving(true);
        API.post(`/sites/periods/`, defaultData)
            .then((res) => {
            refetchPeriods();
            toast.success("새 기간이 추가되었습니다.");
        })
            .catch((error) => {
            toast.error(`기간 추가 실패: ${error.message}`);
        })
            .finally(() => {
            setIsPeriodSaving(false);
        });
    };
    const createPackagePrice = (period_id) => {
        setIsPriceSaving(true);
        API.post(`/package/package-price/`, {
            period: period_id,
            package: id,
            ...buildGroupedPricePayload(),
        })
            .then((res) => {
            refetchPeriods();
            toast.success("가격 정보가 추가되었습니다.");
        })
            .catch((error) => {
            toast.error(`가격 추가 실패: ${error.message}`);
        })
            .finally(() => {
            setIsPriceSaving(false);
        });
    };
    const updatePackagePrice = (pp_id, data) => {
        setIsPriceSaving(true);
        API.put(`/package/package-price/${pp_id}/`, {
            ...data,
            package: id
        })
            .then((res) => {
            refetchPeriods();
            toast.success("가격이 저장되었습니다.");
        })
            .catch((error) => {
            toast.error(`가격 저장 실패: ${error.message}`);
        })
            .finally(() => {
            setIsPriceSaving(false);
        });
    };
    const deletePeriod = (period_id) => {
        setDeleteContext({ type: 'PERIOD', id: period_id, title: "기간 삭제", message: "기간을 삭제하시겠습니까?" });
        setDeleteModal(true);
    };
    const deletePackagePrice = (package_price_id) => {
        setDeleteContext({ type: 'PRICE', id: package_price_id, title: "가격 비활성화", message: "가격 정보를 삭제하고 비활성화 하시겠습니까?" });
        setDeleteModal(true);
    };
    const deletePackageImage = (package_image_id) => {
        setDeleteContext({ type: 'IMAGE', id: package_image_id, title: "이미지 삭제", message: "이미지 정보가 삭제되며 되돌릴 수 없습니다. 정말로 삭제하시겠습니까?" });
        setDeleteModal(true);
    };
    const handleDelete = () => {
        if (!deleteContext)
            return;
        const { type, id } = deleteContext;
        if (type === 'PERIOD') {
            API.delete(`/sites/periods/${id}/`)
                .then(() => {
                refetchPeriods();
                toast.success("기간이 삭제되었습니다.");
                setDeleteModal(false);
            })
                .catch((error) => toast.error(error.message));
        }
        else if (type === 'PRICE') {
            API.delete(`/package/package-price/${id}/`)
                .then(() => {
                refetchPeriods();
                toast.success("가격 정보가 삭제되었습니다.");
                setDeleteModal(false);
            })
                .catch((error) => toast.error(error.message));
        }
        else if (type === 'IMAGE') {
            API.delete(`/package/package-images/${id}/`)
                .then(() => {
                updateDatas(id);
                toast.success("이미지가 삭제되었습니다.");
                setDeleteModal(false);
            })
                .catch((error) => toast.error(error.message));
        }
        else if (type === 'ORDER') {
            // Save Order Logic here
            const len = periods.length;
            const promises = periods.map((period, index) => {
                const newPriority = len - index;
                if (period.priority === newPriority)
                    return null; // Skip if no change
                return API.put(`/sites/periods/${period.id}/`, {
                    ...period,
                    site: SITE_ID,
                    priority: newPriority
                });
            });
            Promise.all(promises.filter(p => p !== null))
                .then(() => {
                refetchPeriods();
                toast.success("순서가 저장되었습니다.");
                setDeleteModal(false);
            })
                .catch((e) => toast.error("순서 저장 중 오류가 발생했습니다."));
        }
    };
    const dragProps = {
        onDragEnd(fromIndex, toIndex) {
            const data = [...periods];
            const item = data.splice(fromIndex, 1)[0];
            data.splice(toIndex, 0, item);
            setPeriods(data);
        },
        nodeSelector: "li",
        handleSelector: ".drag-handle", // Grab the specific handle class
    };
    const saveOrder = async () => {
        setDeleteContext({
            type: 'ORDER',
            title: "순서 저장",
            message: "순서를 저장하여 우선순위를 변경하시겠습니까?",
            btnText: "저장",
            btnColor: "success"
        });
        setDeleteModal(true);
    };
    return (<React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="상품 등록 및 수정" pageTitle="예약"/>

          <Row className="g-4 mb-3">
            <div className="col-sm-auto ml-4">
              <div>
                <h4 className="card-title mb-0 flex-grow-1">상품 등록 및 수정</h4>
              </div>
            </div>
            <div className="col-sm-3 ms-auto">
              <div className="d-flex justify-content-sm-end gap-2">
                {(id === "0" || isCopyMode) ? (<Button color="success" className="add-btn me-1" onClick={sendData} id="update-btn" disabled={isPackageSaving || isImageProcessing}>
                    {isPackageSaving ? '생성 중...' : isImageProcessing ? '이미지 처리 중...' : '생성'}
                  </Button>) : (<Button color="success" className="add-btn me-1" onClick={updateData} id="create-btn" disabled={isPackageSaving || isImageProcessing}>
                    {isPackageSaving ? '수정 중...' : isImageProcessing ? '이미지 처리 중...' : '수정'}
                  </Button>)}
                <Button color="danger" className="add-btn me-1" onClick={async () => {
            if (!await confirmDiscardChanges())
                return;
            navigate(`/reservation-products`);
        }} id="cancel-btn" disabled={isPackageSaving || isImageProcessing}>
                  Cancel
                </Button>
              </div>
            </div>
          </Row>

          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody onChangeCapture={markDirty} onInputCapture={markDirty}>
                  <div className="live-preview">
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label htmlFor="productName" className="form-label">
                            상품명 {isLodgePackage ? <span style={{ color: "red" }}>* 숙박패키지</span> : null}
                          </Label>
                          <Input type="text" className="form-control" placeholder="상품명" id="productName" value={name} onChange={(e) => setName(e.target.value)} maxLength="100"/>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label htmlFor="packageTagline" className="form-label">
                            태그라인
                          </Label>
                          <Input type="text" className="form-control" placeholder="예: 가족, 바비큐, 야간" id="packageTagline" value={tagline} onChange={(e) => setTagline(e.target.value)} maxLength="255"/>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label htmlFor="ForminputState" className="form-label">
                            상태
                          </Label>
                          <select value={state} onChange={(e) => setState(e.target.value)} id="ForminputState" className="form-select" data-choices data-choices-sorting="true">
                            <option key="1" value="노출중">
                              노출중
                            </option>
                            <option key="2" value="숨김">
                              숨김
                            </option>
                            <option key="3" value="품절">
                              품절
                            </option>
                          </select>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label htmlFor="spotCount" className="form-label">
                            좌석수
                            <div className="d-flex align-items-center gap-2">
                              <Input type="checkbox" id="formCheck6" checked={hasSpot} onChange={(e) => {
            setHasSpot(e.target.checked);
        }}/>
                              <Label className="m-0 cursor-pointer" htmlFor="formCheck6">
                                좌석지정 (해제할 경우 자유석)
                              </Label>
                            </div>
                          </Label>
                          <Input type="number" className="form-control" placeholder="좌석수" id="spotCount" value={count} onChange={(e) => {
            const val = Number(e.target.value);
            setCount(val < 0 || !e.target.value ? 0 : val);
        }} min="0"/>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label htmlFor="seatImg" className="form-label">
                            자리배치도
                          </Label>
                          <Input className="form-control" type="file" id="seatImg" accept="image/jpg, image/jpeg, image/png" ref={seatImgInputRef} onChange={(e) => handleImageFiles(e, "seat")}/>
                          {loadedSeatImg ? (<div className="mt-2">
                              <a href={loadedSeatImg} target="_blank" rel="noreferrer">
                                <img width="80px" src={loadedSeatImg} alt={`${name} seating arrangement`}/>
                              </a>
                            </div>) : (<span style={{ color: "red" }}>* 필수</span>)}
                        </div>
                      </Col>

                      <Col md={6} hidden={id !== "0"}>
                        <div className="mb-3">
                          <Label htmlFor="minDate" className="form-label">
                            최소예약일 <span style={{ color: "red" }}>*</span> 2일 이상으로 입력 시 숙박 상품으로 등록됩니다.
                          </Label>
                          <Input type="number" className="form-control" placeholder="최소예약일" id="minDate" value={minDate} onChange={(e) => {
            const val = Number(e.target.value);
            setMinDate(val < 0 ? 0 : val);
        }} min="0"/>
                        </div>
                      </Col>
                      <Col md={6} hidden={id !== "0"}>
                        <div className="mb-3">
                          <Label htmlFor="maxDate" className="form-label">
                            최대예약일 <span style={{ color: "red" }}>*</span> 2일 이상으로 입력 시 숙박 상품으로 등록됩니다.
                          </Label>
                          <Input type="number" className="form-control" placeholder="최대예약일" id="maxDate" value={maxDate} onChange={(e) => {
            const val = Number(e.target.value);
            setMaxDate(val < 0 ? 0 : val);
        }} min="0"/>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="mb-3">
                          <Label htmlFor="detailImg" className="form-label">
                            상세이미지
                          </Label>
                          <Input className="form-control" type="file" id="detailImg" accept="image/jpg, image/jpeg, image/png" ref={detailImgInputRef} onChange={(e) => handleImageFiles(e, "detail")} multiple/>
                          {loadedDetailImg ? (<Col className="mt-2 d-flex flex-col gap-1 flex-wrap">
                              {loadedDetailImg.map((item) => (<div key={item.key} style={{ position: "relative" }}>
                                  <a href={item.src} target="_blank" rel="noreferrer">
                                    <img width="80px" src={item.src} alt={`${item.key} ${item.altText}`}/>
                                  </a>
                                  <div style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    cursor: "pointer",
                    fontSize: "14px",
                    background: "#eee",
                    borderRadius: "20%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    paddingInline: "2px",
                }} onClick={(e) => deletePackageImage(item.key)}>
                                    <i className="ri-delete-bin-5-fill text-danger"></i>
                                  </div>
                                </div>))}
                            </Col>) : null}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label htmlFor="choices-multiple-default" className="form-label">
                            옵션 상품 연결
                          </Label>
                          <Select value={selectedMulti} isMulti={true} onChange={(values) => {
            handleMulti(values);
        }} options={optionProducts}/>
                        </div>
                      </Col>
                    </Row>
                  </div>
                  <div className="mt-3">
                    <Button color="primary" size="md" className="btn btn-soft-secondary" onClick={() => {
            setAdvancedSettings(!advancedSettings);
        }}>
                      고급 설정 - {advancedSettings ? "숨기기" : "보이기"}
                    </Button>

                    {advancedSettings ? (<Row className="mt-3">
                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="minPersonnel" className="form-label">
                              최소인원수 (옵션으로 인원 추가하는 경우)
                            </Label>
                            <Input type="number" className="form-control" placeholder="최소인원수" id="minPersonnel" value={stdPersonnel} onChange={(e) => {
                const val = Number(e.target.value);
                setStdPersonnel(val < 0 ? 0 : val);
            }} min="0"/>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="maxPersonnel" className="form-label">
                              최대인원수 (1개 예약 시, 옵션으로 인원 추가하는 경우)
                            </Label>
                            <Input type="number" className="form-control" placeholder="최대인원수" id="maxPersonnel" value={maxPersonnel} onChange={(e) => {
                const val = Number(e.target.value);
                setMaxPersonnel(val < 0 ? 0 : val);
            }} min="0"/>
                          </div>
                        </Col>
                        {/* <Col md={6}>
            <div className="mb-3">
              <Label htmlFor="exceedPrice" className="form-label">
                초과인원금액
              </Label>
              <Input
                type="number"
                className="form-control"
                placeholder="초과인원금액"
                id="exceedPrice"
                value={exceedPrice === 0 ? "" : exceedPrice}
                onChange={(e) => setExceedPrice(e.target.value < 0 || !e.target.value ? 0 : e.target.value)}
                min="0"
              />
            </div>
          </Col> */}

                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="allowFreeBooking" className="form-label">
                              무료예약허용 (테이블링)
                            </Label>
                            <div>
                              <Input style={{ marginLeft: 15, marginRight: 8 }} className="form-check-input" type="checkbox" id="allowFreeBooking" checked={allowFreeBooking} onChange={(e) => {
                setAllowFreeBooking(e.target.checked);
            }}/>
                              <Label className="form-check-label" htmlFor="allowFreeBooking">
                                허용하기 (결제할 금액이 0원인 경우, 결제 건너뛰기 허용)
                              </Label>
                            </div>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="maxPersonnelDay" className="form-label">
                              최대인원수 (일일)
                            </Label>
                            <Input type="number" className="form-control" placeholder="최대인원수" id="maxPersonnelDay" value={maxPersonnelDay} onChange={(e) => setMaxPersonnelDay(Number(e.target.value) || 0)} min="0"/>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="priority" className="form-label">
                              표시 순서 - 오름차순
                            </Label>
                            <Input type="number" className="form-control" placeholder="레벨" id="priority" value={priority} onChange={(e) => setPriority(Number(e.target.value) || 0)} min="0"/>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="optionRequired" className="form-label">
                              옵션 필수 여부
                            </Label>
                            <div>
                              <Input style={{ marginLeft: 15, marginRight: 8 }} className="form-check-input" type="checkbox" id="optionRequired" checked={optionRequired} onChange={(e) => {
                setOptionRequired(e.target.checked);
            }}/>
                              <Label className="form-check-label" htmlFor="optionRequired">
                                옵션 선택해야 결제 가능
                              </Label>
                            </div>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="optionRequiredType" className="form-label">
                              옵션 필수 타입 {optionRequiredType?.value}
                            </Label>
                            <Select value={optionRequiredType} onChange={(values) => {
                markDirty();
                setOptionRequiredType(values);
            }} options={optionRequiredTypeSelect} backspaceRemovesValue={true} isClearable={true}/>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="orderAvailableAt" className="form-label">
                              예약 결제 가능 시작일시
                            </Label>
                            <Input type="datetime-local" className="form-control" id="orderAvailableAt" value={orderAvailableAt} onChange={(e) => {
                setOrderAvailableAt(e.target.value);
            }}/>
                            <div className="form-text">비우면 지점 설정을 따르고, 입력하면 이 상품의 결제 오픈 시각을 별도로 설정합니다.</div>
                            {!!orderAvailableAt && <Button color="danger" size="sm" onClick={() => {
                markDirty();
                setOrderAvailableAt("");
            }}>
                              x
                            </Button>}
                          </div>
                        </Col>

                      </Row>) : null}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">기간 및 가격 설정</h5>
                  <div>
                    <Button size="sm" color="primary" className="me-2" onClick={handlePeriodAdd} disabled={isPeriodSaving}>
                      {isPeriodSaving ? '추가 중...' : '+ 기간 추가'}
                    </Button>
                    <Button size="sm" color="success" onClick={saveOrder}>
                      순서 저장
                    </Button>
                  </div>
                </CardHeader>
                <CardBody className="bg-light">
                  <ReactDragListView {...dragProps}>
                    <ul className="list-unstyled mb-0">
                      {periods.map((period, index) => {
            const packagePrice = period.package_prices.find((item) => {
                return Number(item["package_id"]) === Number(id);
            });
            return (<li key={period.id}>
                            <PeriodItem period={period} packagePrice={packagePrice} onPeriodUpdate={handlePeriodUpdate} onPeriodDelete={deletePeriod} onPriceCreate={createPackagePrice} onPriceUpdate={updatePackagePrice} onPriceDelete={deletePackagePrice} dragHandleProps={{ className: "drag-handle" }} isPeriodSaving={isPeriodSaving} isPriceSaving={isPriceSaving}/>
                          </li>);
        })}
                    </ul>
                  </ReactDragListView>
                  {periods.length === 0 && (<div className="text-center p-4 text-muted">
                      등록된 기간이 없습니다. 기간을 추가해주세요.
                    </div>)}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col lg={4} md={6}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">상세정보</h4>
                </CardHeader>
                <CardBody>
                  <div className="snow-editor" style={{ height: 300 }}>
                    <MyEditor value={descript} onChange={(value) => {
                      markDirty();
                      setDescript(value);
                    }} uid={1}/>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg={4} md={6}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">이용안내</h4>
                </CardHeader>
                <CardBody>
                  <div className="snow-editor" style={{ height: 300 }}>
                    <MyEditor value={usingInfo} onChange={(value) => {
                      markDirty();
                      setUsingInfo(value);
                    }} uid={2}/>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg={4} md={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">유의사항</h4>
                </CardHeader>
                <CardBody>
                  <div className="snow-editor" style={{ height: 300 }}>
                    <MyEditor value={instruction} onChange={(value) => {
                      markDirty();
                      setInstruction(value);
                    }} uid={3}/>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <DeleteModal show={deleteModal} onDeleteClick={handleDelete} onCloseClick={() => setDeleteModal(false)} modalTitle={deleteContext?.title} message={deleteContext?.message} btnText={deleteContext?.btnText} btnColor={deleteContext?.btnColor}/>
          <Toaster position="top-center"/>
        </Container>
      </div>
    </React.Fragment>);
};
export default ReservationAddProduct;
